'use client';

import type { ReactNode } from 'react';
import { useTranslation } from 'next-i18next/pages';

import type { ModalProps } from '../modal';
import { Title } from '../title';
import { openConfirmPrompt, WarningIcon } from './open-confirm-prompt';

interface UseConfirmModalProps {
  icon?: ReactNode;
  title?: string;
  children?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ModalProps['variant'];
}

export interface HandleConfirmProps {
  confirmationMessage?: ReactNode | string;
  /** `data-testid` for the confirm button, for the Playwright suites that click it by test id. */
  confirmTestId?: string;
  onCancel?: () => void;
  /**
   * Called whenever the modal closes, regardless of how: the confirm/cancel
   * buttons, the ✕, Escape, or an overlay click. Unlike `onCancel` (which fires
   * only for the cancel button) this covers every dismissal path.
   */
  onClose?: () => void;
  onConfirm: () => void;
}

/**
 * Imperative "are you sure?" prompt, fired from an event handler with a static message. For a modal
 * with content the user edits, use `ActionModal` from `@highlight-cp/core-ui-compositions` instead —
 * `openConfirmModal` snapshots `children` at call time, so live form state never reaches it.
 */
export const useConfirmModal = (props: UseConfirmModalProps) => {
  const { icon, title, children, confirmText, cancelText, variant } = props;
  const { t } = useTranslation();

  const confirm = (handlers: HandleConfirmProps) =>
    openConfirmPrompt(
      {
        icon: icon ?? <WarningIcon />,
        defaultTitle: <Title order={4}>{t('Are you sure?')}</Title>,
        confirmLabel: confirmText ?? t('Confirm'),
        cancelLabel: cancelText ?? t('Cancel'),
        size: 'lg',
        variant,
      },
      { ...handlers, children, title },
    );

  return {
    confirm,
  };
};
